<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import LanguageSelector from '../components/LanguageSelector.vue'
import clubApplicationService from '../services/clubApplication.service'
import type { PublicClubInfo } from '../types/clubApplication.types'

const route = useRoute()
const { t, locale } = useI18n()

const token = computed(() => route.params.token as string)
const currentLang = computed(() => locale.value || localStorage.getItem('language') || 'pl')

// Page state
const club = ref<PublicClubInfo | null>(null)
const loadingClub = ref(true)
const linkState = ref<'ok' | 'invalid' | 'disabled'>('ok')

// Form state
const form = ref({
  email: '',
  password: '',
  password_confirmation: '',
  first_name: '',
  last_name: '',
  gender: '',
  birth_date: '',
  phone: '',
  guardian_type: '',
  guardian1_first_name: '',
  guardian1_last_name: '',
  guardian_phone: '',
  license_number: '',
  guardian2_first_name: '',
  guardian2_last_name: '',
  street: '',
  postal_code: '',
  city: '',
  school_name: '',
  terms_accepted: false,
  // Honeypot — hidden from people, bots tend to fill it
  website: ''
})

const showOptional = ref(false)
const submitting = ref(false)
const error = ref<string | null>(null)
const validationErrors = ref<Record<string, string[]>>({})
const success = ref<{ message: string; requiresActivation: boolean } | null>(null)

// 18+ on birth date → parent data optional, player phone required
const isAdult = computed(() => {
  if (!form.value.birth_date) return false
  const birth = new Date(form.value.birth_date)
  if (isNaN(birth.getTime())) return false
  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const monthDiff = today.getMonth() - birth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--
  return age >= 18
})

const today = new Date().toISOString().split('T')[0]

const fetchClub = async () => {
  loadingClub.value = true
  try {
    const response = await clubApplicationService.getPublicClub(token.value)
    club.value = response.club
    linkState.value = 'ok'
  } catch (err: any) {
    if (err.response?.status === 403 && err.response?.data?.error === 'signup_disabled') {
      linkState.value = 'disabled'
      club.value = err.response.data.club || null
    } else {
      linkState.value = 'invalid'
    }
  } finally {
    loadingClub.value = false
  }
}

const orNull = (value: string) => (value.trim() === '' ? null : value.trim())

const handleSubmit = async () => {
  error.value = null
  validationErrors.value = {}

  if (form.value.password !== form.value.password_confirmation) {
    validationErrors.value = { password_confirmation: [t('register.passwordsNotMatch')] }
    return
  }

  if (!form.value.terms_accepted) {
    error.value = t('register.mustAcceptTerms')
    return
  }

  submitting.value = true
  try {
    const f = form.value
    const response = await clubApplicationService.submitSignup(token.value, {
      email: f.email.trim(),
      password: f.password,
      password_confirmation: f.password_confirmation,
      first_name: f.first_name.trim(),
      last_name: f.last_name.trim(),
      gender: f.gender,
      birth_date: f.birth_date,
      phone: orNull(f.phone),
      guardian_type: f.guardian_type || null,
      guardian1_first_name: orNull(f.guardian1_first_name),
      guardian1_last_name: orNull(f.guardian1_last_name),
      guardian_phone: orNull(f.guardian_phone),
      license_number: orNull(f.license_number),
      guardian2_first_name: orNull(f.guardian2_first_name),
      guardian2_last_name: orNull(f.guardian2_last_name),
      street: orNull(f.street),
      postal_code: orNull(f.postal_code),
      city: orNull(f.city),
      school_name: orNull(f.school_name),
      terms_accepted: f.terms_accepted,
      language: localStorage.getItem('language') || 'pl',
      website: f.website
    })
    success.value = {
      message: response.message,
      requiresActivation: response.requires_activation
    }
    window.scrollTo(0, 0)
  } catch (err: any) {
    console.error('Club signup error:', err)
    const data = err.response?.data
    if (err.response?.status === 403 && data?.error === 'signup_disabled') {
      linkState.value = 'disabled'
    } else if (err.response?.status === 404) {
      linkState.value = 'invalid'
    } else if (data?.errors) {
      validationErrors.value = data.errors
      // Open the optional section if an error sits there
      const optionalFields = ['license_number', 'guardian2_first_name', 'guardian2_last_name', 'street', 'postal_code', 'city', 'school_name']
      if (!isAdult.value) optionalFields.push('phone')
      if (optionalFields.some(field => data.errors[field])) showOptional.value = true
      error.value = t('joinClub.fixErrors')
    } else {
      error.value = data?.message || t('joinClub.errorSubmitting')
    }
    window.scrollTo(0, 0)
  } finally {
    submitting.value = false
  }
}

const getFieldError = (field: string): string | null => validationErrors.value[field]?.[0] || null

const inputClass = (field: string) => [
  'w-full px-3 py-2 text-sm border-2 rounded focus:outline-none bg-white',
  getFieldError(field) ? 'border-red-500 focus:border-red-600' : 'border-gray-300 focus:border-blue-600'
]

onMounted(() => {
  fetchClub()
})
</script>

<template>
  <main class="min-h-screen flex justify-center px-4 py-8 bg-gray-50">
    <div class="w-full max-w-2xl">
      <!-- Language Selector -->
      <div class="mb-4 flex justify-end">
        <LanguageSelector />
      </div>

      <!-- Loading -->
      <div v-if="loadingClub" class="flex justify-center py-12">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>

      <template v-else>
        <!-- Club header -->
        <div v-if="club" class="flex flex-col items-center text-center mb-4">
          <img v-if="club.logo_url" :src="club.logo_url" :alt="club.name" class="h-20 w-20 object-contain mb-2">
          <p class="text-lg font-semibold text-gray-900">{{ club.name }}</p>
        </div>

        <!-- Invalid link -->
        <div v-if="linkState === 'invalid'" class="bg-white border-2 border-gray-300 rounded shadow-md p-6 text-center">
          <h2 class="text-xl font-semibold text-gray-800 mb-2">{{ t('joinClub.invalidLinkTitle') }}</h2>
          <p class="text-sm text-gray-600">{{ t('joinClub.invalidLinkText') }}</p>
        </div>

        <!-- Signups disabled -->
        <div v-else-if="linkState === 'disabled'" class="bg-white border-2 border-gray-300 rounded shadow-md p-6 text-center">
          <h2 class="text-xl font-semibold text-gray-800 mb-2">{{ t('joinClub.disabledTitle') }}</h2>
          <p class="text-sm text-gray-600">{{ t('joinClub.disabledText') }}</p>
        </div>

        <!-- Success -->
        <div v-else-if="success" class="bg-white border-2 border-gray-300 rounded shadow-md p-6">
          <div class="p-3 bg-green-50 border-2 border-green-300 rounded mb-4">
            <p class="text-sm text-green-800 font-medium">{{ success.message }}</p>
          </div>
          <p v-if="success.requiresActivation" class="text-sm text-gray-700 mb-4">{{ t('joinClub.checkEmail') }}</p>
          <p v-else class="text-sm text-gray-700 mb-4">{{ t('joinClub.waitForClub') }}</p>
          <router-link to="/login" class="text-sm text-blue-600 hover:text-blue-700 font-medium">
            {{ t('joinClub.goToLogin') }}
          </router-link>
        </div>

        <!-- Form -->
        <div v-else class="bg-white border-2 border-gray-300 rounded shadow-md p-4 md:p-6">
          <h1 class="text-xl font-semibold text-gray-800 mb-1">{{ t('joinClub.title') }}</h1>
          <p class="text-sm text-gray-600 mb-4">{{ t('joinClub.subtitle') }}</p>

          <div v-if="error" class="mb-4 p-3 bg-red-50 border-2 border-red-300 rounded">
            <p class="text-sm text-red-700">{{ error }}</p>
          </div>

          <form @submit.prevent="handleSubmit" novalidate>
            <!-- Honeypot -->
            <div class="hidden" aria-hidden="true">
              <label for="website">Website</label>
              <input id="website" v-model="form.website" type="text" tabindex="-1" autocomplete="off">
            </div>

            <!-- Required data -->
            <div class="border-2 border-gray-300 rounded p-4 mb-4">
              <h2 class="text-lg font-semibold text-gray-900 mb-4">{{ t('players.requiredData') }}</h2>

              <!-- Account -->
              <h3 class="text-sm font-semibold text-gray-800 mb-2">{{ t('joinClub.accountSection') }}</h3>
              <div class="mb-3 p-3 bg-blue-50 border border-blue-200 rounded">
                <p class="text-xs text-blue-800">{{ t('joinClub.existingAccountInfo') }}</p>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div class="md:col-span-2">
                  <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
                    {{ t('register.email') }} <span class="text-red-600">{{ t('common.required') }}</span>
                  </label>
                  <input id="email" v-model="form.email" type="email" autocomplete="email" required :class="inputClass('email')">
                  <p v-if="getFieldError('email')" class="mt-1 text-xs text-red-600">{{ getFieldError('email') }}</p>
                </div>
                <div>
                  <label for="password" class="block text-sm font-medium text-gray-700 mb-1">
                    {{ t('register.password') }} <span class="text-red-600">{{ t('common.required') }}</span>
                  </label>
                  <input id="password" v-model="form.password" type="password" autocomplete="new-password" required :class="inputClass('password')">
                  <p v-if="getFieldError('password')" class="mt-1 text-xs text-red-600">{{ getFieldError('password') }}</p>
                </div>
                <div>
                  <label for="password_confirmation" class="block text-sm font-medium text-gray-700 mb-1">
                    {{ t('register.confirmPassword') }} <span class="text-red-600">{{ t('common.required') }}</span>
                  </label>
                  <input id="password_confirmation" v-model="form.password_confirmation" type="password" autocomplete="new-password" required :class="inputClass('password_confirmation')">
                  <p v-if="getFieldError('password_confirmation')" class="mt-1 text-xs text-red-600">{{ getFieldError('password_confirmation') }}</p>
                </div>
              </div>

              <!-- Player -->
              <h3 class="text-sm font-semibold text-gray-800 mb-2">{{ t('joinClub.playerSection') }}</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label for="first_name" class="block text-sm font-medium text-gray-700 mb-1">
                    {{ t('players.firstName') }} <span class="text-red-600">{{ t('common.required') }}</span>
                  </label>
                  <input id="first_name" v-model="form.first_name" type="text" required :class="inputClass('first_name')">
                  <p v-if="getFieldError('first_name')" class="mt-1 text-xs text-red-600">{{ getFieldError('first_name') }}</p>
                </div>
                <div>
                  <label for="last_name" class="block text-sm font-medium text-gray-700 mb-1">
                    {{ t('players.lastName') }} <span class="text-red-600">{{ t('common.required') }}</span>
                  </label>
                  <input id="last_name" v-model="form.last_name" type="text" required :class="inputClass('last_name')">
                  <p v-if="getFieldError('last_name')" class="mt-1 text-xs text-red-600">{{ getFieldError('last_name') }}</p>
                </div>
                <div>
                  <label for="gender" class="block text-sm font-medium text-gray-700 mb-1">
                    {{ t('players.gender') }} <span class="text-red-600">{{ t('common.required') }}</span>
                  </label>
                  <select id="gender" v-model="form.gender" required :class="inputClass('gender')">
                    <option value="">{{ t('players.selectGender') }}</option>
                    <option value="M">{{ t('players.male') }}</option>
                    <option value="W">{{ t('players.female') }}</option>
                  </select>
                  <p v-if="getFieldError('gender')" class="mt-1 text-xs text-red-600">{{ getFieldError('gender') }}</p>
                </div>
                <div>
                  <label for="birth_date" class="block text-sm font-medium text-gray-700 mb-1">
                    {{ t('players.birthDate') }} <span class="text-red-600">{{ t('common.required') }}</span>
                  </label>
                  <input id="birth_date" v-model="form.birth_date" type="date" :max="today" required :class="inputClass('birth_date')">
                  <p v-if="getFieldError('birth_date')" class="mt-1 text-xs text-red-600">{{ getFieldError('birth_date') }}</p>
                </div>
                <!-- Adult: player phone is required -->
                <div v-if="isAdult" class="md:col-span-2">
                  <label for="phone" class="block text-sm font-medium text-gray-700 mb-1">
                    {{ t('joinClub.playerPhone') }} <span class="text-red-600">{{ t('common.required') }}</span>
                  </label>
                  <input id="phone" v-model="form.phone" type="tel" autocomplete="tel" required :class="inputClass('phone')">
                  <p v-if="getFieldError('phone')" class="mt-1 text-xs text-red-600">{{ getFieldError('phone') }}</p>
                </div>
              </div>

              <!-- Parent / guardian (required for minors) -->
              <h3 class="text-sm font-semibold text-gray-800 mb-2">{{ t('players.guardianData') }}</h3>
              <p v-if="isAdult" class="text-xs text-gray-500 mb-2">{{ t('joinClub.guardianOptionalAdult') }}</p>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label for="guardian1_first_name" class="block text-sm font-medium text-gray-700 mb-1">
                    {{ t('joinClub.guardianFirstName') }} <span v-if="!isAdult" class="text-red-600">{{ t('common.required') }}</span>
                  </label>
                  <input id="guardian1_first_name" v-model="form.guardian1_first_name" type="text" :required="!isAdult" :class="inputClass('guardian1_first_name')">
                  <p v-if="getFieldError('guardian1_first_name')" class="mt-1 text-xs text-red-600">{{ getFieldError('guardian1_first_name') }}</p>
                </div>
                <div>
                  <label for="guardian1_last_name" class="block text-sm font-medium text-gray-700 mb-1">
                    {{ t('joinClub.guardianLastName') }} <span v-if="!isAdult" class="text-red-600">{{ t('common.required') }}</span>
                  </label>
                  <input id="guardian1_last_name" v-model="form.guardian1_last_name" type="text" :required="!isAdult" :class="inputClass('guardian1_last_name')">
                  <p v-if="getFieldError('guardian1_last_name')" class="mt-1 text-xs text-red-600">{{ getFieldError('guardian1_last_name') }}</p>
                </div>
                <div>
                  <label for="guardian_type" class="block text-sm font-medium text-gray-700 mb-1">
                    {{ t('players.guardianType') }} <span v-if="!isAdult" class="text-red-600">{{ t('common.required') }}</span>
                  </label>
                  <select id="guardian_type" v-model="form.guardian_type" :required="!isAdult" :class="inputClass('guardian_type')">
                    <option value="">{{ t('players.selectType') }}</option>
                    <option value="parent">{{ t('players.parent') }}</option>
                    <option value="guardian">{{ t('players.guardian') }}</option>
                  </select>
                  <p v-if="getFieldError('guardian_type')" class="mt-1 text-xs text-red-600">{{ getFieldError('guardian_type') }}</p>
                </div>
                <div>
                  <label for="guardian_phone" class="block text-sm font-medium text-gray-700 mb-1">
                    {{ t('joinClub.guardianPhone') }} <span v-if="!isAdult" class="text-red-600">{{ t('common.required') }}</span>
                  </label>
                  <input id="guardian_phone" v-model="form.guardian_phone" type="tel" autocomplete="tel" :required="!isAdult" :class="inputClass('guardian_phone')">
                  <p v-if="getFieldError('guardian_phone')" class="mt-1 text-xs text-red-600">{{ getFieldError('guardian_phone') }}</p>
                </div>
              </div>
            </div>

            <!-- Optional data (collapsed by default) -->
            <div class="border-2 border-gray-300 rounded mb-4">
              <button
                type="button"
                @click="showOptional = !showOptional"
                class="w-full flex items-center justify-between p-4 text-left"
                :aria-expanded="showOptional"
              >
                <span class="text-lg font-semibold text-gray-900">{{ t('players.optionalData') }}</span>
                <svg
                  :class="['w-5 h-5 text-gray-600 transition-transform', { 'rotate-180': showOptional }]"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
                </svg>
              </button>

              <div v-show="showOptional" class="px-4 pb-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label for="license_number" class="block text-sm font-medium text-gray-700 mb-1">{{ t('players.licenseNumber') }}</label>
                    <input id="license_number" v-model="form.license_number" type="text" :class="inputClass('license_number')">
                    <p v-if="getFieldError('license_number')" class="mt-1 text-xs text-red-600">{{ getFieldError('license_number') }}</p>
                  </div>
                  <div v-if="!isAdult">
                    <label for="phone_optional" class="block text-sm font-medium text-gray-700 mb-1">{{ t('joinClub.playerPhone') }}</label>
                    <input id="phone_optional" v-model="form.phone" type="tel" :class="inputClass('phone')">
                    <p v-if="getFieldError('phone')" class="mt-1 text-xs text-red-600">{{ getFieldError('phone') }}</p>
                  </div>
                  <div>
                    <label for="guardian2_first_name" class="block text-sm font-medium text-gray-700 mb-1">{{ t('joinClub.guardian2FirstName') }}</label>
                    <input id="guardian2_first_name" v-model="form.guardian2_first_name" type="text" :class="inputClass('guardian2_first_name')">
                    <p v-if="getFieldError('guardian2_first_name')" class="mt-1 text-xs text-red-600">{{ getFieldError('guardian2_first_name') }}</p>
                  </div>
                  <div>
                    <label for="guardian2_last_name" class="block text-sm font-medium text-gray-700 mb-1">{{ t('joinClub.guardian2LastName') }}</label>
                    <input id="guardian2_last_name" v-model="form.guardian2_last_name" type="text" :class="inputClass('guardian2_last_name')">
                    <p v-if="getFieldError('guardian2_last_name')" class="mt-1 text-xs text-red-600">{{ getFieldError('guardian2_last_name') }}</p>
                  </div>
                </div>

                <h3 class="text-sm font-semibold text-gray-800 mb-2">{{ t('players.addressData') }}</h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="md:col-span-2">
                    <label for="street" class="block text-sm font-medium text-gray-700 mb-1">{{ t('players.street') }}</label>
                    <input id="street" v-model="form.street" type="text" autocomplete="street-address" :class="inputClass('street')">
                    <p v-if="getFieldError('street')" class="mt-1 text-xs text-red-600">{{ getFieldError('street') }}</p>
                  </div>
                  <div>
                    <label for="postal_code" class="block text-sm font-medium text-gray-700 mb-1">{{ t('players.postalCode') }}</label>
                    <input id="postal_code" v-model="form.postal_code" type="text" autocomplete="postal-code" :class="inputClass('postal_code')">
                    <p v-if="getFieldError('postal_code')" class="mt-1 text-xs text-red-600">{{ getFieldError('postal_code') }}</p>
                  </div>
                  <div>
                    <label for="city" class="block text-sm font-medium text-gray-700 mb-1">{{ t('players.city') }}</label>
                    <input id="city" v-model="form.city" type="text" autocomplete="address-level2" :class="inputClass('city')">
                    <p v-if="getFieldError('city')" class="mt-1 text-xs text-red-600">{{ getFieldError('city') }}</p>
                  </div>
                  <div class="md:col-span-2">
                    <label for="school_name" class="block text-sm font-medium text-gray-700 mb-1">{{ t('players.school') }}</label>
                    <input id="school_name" v-model="form.school_name" type="text" :class="inputClass('school_name')">
                    <p v-if="getFieldError('school_name')" class="mt-1 text-xs text-red-600">{{ getFieldError('school_name') }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Terms -->
            <div class="flex items-start mb-4">
              <input
                id="terms"
                v-model="form.terms_accepted"
                type="checkbox"
                required
                class="h-4 w-4 text-blue-600 border-gray-300 rounded mt-1"
              >
              <label for="terms" class="ml-2 text-sm text-gray-600">
                {{ t('register.acceptTerms') }}
                <a :href="`/terms?lang=${currentLang}`" target="_blank" class="text-blue-600 hover:text-blue-700 underline">{{ t('register.appTerms') }}</a>
                {{ t('register.and') }}
                <a :href="`/privacy-policy?lang=${currentLang}`" target="_blank" class="text-blue-600 hover:text-blue-700 underline">{{ t('register.privacyPolicy') }}</a>
              </label>
            </div>
            <p v-if="getFieldError('terms_accepted')" class="-mt-3 mb-4 text-xs text-red-600">{{ getFieldError('terms_accepted') }}</p>

            <button
              type="submit"
              :disabled="submitting"
              class="w-full py-2 px-4 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ submitting ? t('joinClub.submitting') : t('joinClub.submit') }}
            </button>
          </form>
        </div>
      </template>
    </div>
  </main>
</template>
